import { defineStore } from "pinia";
import { httpsCallable } from "firebase/functions";
import {
  collection,
  doc,
  getDocs,
  getCountFromServer,
  limit,
  onSnapshot,
  orderBy,
  query,
  startAfter,
  type QueryConstraint,
  type DocumentData,
  type QueryDocumentSnapshot,
} from "firebase/firestore";
import { firestore, functions } from "@/firebase";
import { BasicResponse } from "functions/src/responseTypes";
import { VaultAssetsSummary } from "functions/src/Vault/VaultInterfaces";

import {
  VaultState,
  VaultSummaryUI,
  VaultAddress,
  VaultAddressUI,
  VaultTransactionUI,
} from "@shared/entities/Vault/VaultInterfaces";

const DEFAULT_ADDRESSES_PAGE_SIZE = 20;
const DEFAULT_TRANSACTIONS_PAGE_SIZE = 15;

const initialSummary: VaultSummaryUI = {
  available: "0.00000000",
  assigned: "0.00000000",
  total: "0.00000000",
  btcPrice: 0,
  updatedDate: undefined,
};

const initialState = (): VaultState => ({
  summary: { ...initialSummary },
  addresses: [],
  transactions: [],
  summaryLoading: false,
  addressesLoading: false,
  transactionsLoading: false,
  summaryUpdatedAt: undefined,
  addressesUpdatedAt: undefined,
  transactionsUpdatedAt: undefined,
  addressesPage: 0,
  addressesPageSize: DEFAULT_ADDRESSES_PAGE_SIZE,
  addressesHasMore: false,
  addressesTotalCount: 0,
  transactionsPage: 0,
  transactionsPageSize: DEFAULT_TRANSACTIONS_PAGE_SIZE,
  transactionsHasMore: false,
  transactionsTotalCount: 0,
});

let summaryUnsubscribe: (() => void) | undefined;
let addressesCursors: Array<QueryDocumentSnapshot<DocumentData> | undefined> =
  [];
let transactionsCursors: Array<
  QueryDocumentSnapshot<DocumentData> | undefined
> = [];

const toDate = (value: any): Date => {
  if (!value) {
    return new Date(NaN);
  }
  if (value instanceof Date) {
    return value;
  }
  if (typeof value === "number") {
    return new Date(value);
  }
  if (typeof value === "string") {
    return new Date(value);
  }
  if (typeof value === "object" && typeof value.toDate === "function") {
    return value.toDate();
  }
  return new Date(value);
};

export const useVaultStore = defineStore("vault", {
  state: (): VaultState => initialState(),
  getters: {
    summaryAvailable: (state) => state.summary.available,
    summaryAssigned: (state) => state.summary.assigned,
    summaryTotal: (state) => state.summary.total,
    summaryBtcPrice: (state) => state.summary.btcPrice,
    summaryUpdatedDate: (state) => state.summary.updatedDate,
    isSummaryLoading: (state) => state.summaryLoading,
    isAddressesLoading: (state) => state.addressesLoading,
    isTransactionsLoading: (state) => state.transactionsLoading,
  },
  actions: {
    clearState() {
      if (summaryUnsubscribe) {
        summaryUnsubscribe();
        summaryUnsubscribe = undefined;
      }
      addressesCursors = [];
      transactionsCursors = [];
      Object.assign(this, initialState());
    },

    async fetchSummary(params: { force?: boolean; vaultId?: string } = {}) {
      const vaultId = params.vaultId || process.env.VUE_APP_VAULT_ID || "main";
      const force = params.force === true;
      if (summaryUnsubscribe && !force) {
        return;
      }
      if (summaryUnsubscribe) {
        summaryUnsubscribe();
        summaryUnsubscribe = undefined;
      }
      this.summaryLoading = true;
      const ref = doc(firestore, "systemVaults", vaultId);
      summaryUnsubscribe = onSnapshot(
        ref,
        (snap) => {
          if (!snap.exists()) {
            this.summary = { ...initialSummary };
            this.summaryUpdatedAt = undefined;
            this.summaryLoading = false;
            return;
          }
          const data = snap.data() as VaultAssetsSummary;
          const btcAsset = data.assets?.find((asset) => asset.symbol === "BTC");
          const btcAmounts = btcAsset?.assetData;
          const summary: VaultSummaryUI = {
            available: btcAmounts
              ? parseFloat(btcAmounts.availableAmount).toFixed(8)
              : "0.00000000",
            assigned: btcAmounts?.allocatedAmount
              ? parseFloat(btcAmounts.allocatedAmount).toFixed(8)
              : "0.00000000",
            total: btcAmounts
              ? parseFloat(btcAmounts.totalAmount).toFixed(8)
              : "0.00000000",
            btcPrice: btcAsset?.exchangeRate
              ? parseFloat(btcAsset.exchangeRate)
              : 0,
            updatedDate: data.dateUpdatedAssetsSummary
              ? toDate(data.dateUpdatedAssetsSummary)
              : undefined,
          };
          this.summary = summary;
          this.summaryUpdatedAt = summary.updatedDate;
          this.summaryLoading = false;
        },
        (error) => {
          console.error("Error listening vault summary", error);
          this.summaryLoading = false;
        }
      );
    },

    async refreshSummary(params: { blockchain?: string } = {}) {
      this.summaryLoading = true;
      const shouldRefreshTransactions = this.transactions.length > 0;
      const shouldRefreshAddresses = this.addresses.length > 0;
      try {
        const updateVaultData = httpsCallable(
          functions,
          "Vault-UpdateVaultData"
        );
        const result = await updateVaultData({
          blockchain: params.blockchain,
        });
        const response = result.data as BasicResponse;
        if (response.OPCODE !== "SUCCESS") {
          console.error("Error updating vault data", response);
        }
      } catch (error) {
        console.error("Error updating vault data", error);
      }

      await Promise.all([
        this.fetchSummary({ force: true }),
        shouldRefreshTransactions
          ? this.fetchTransactions({ force: true, page: this.transactionsPage })
          : Promise.resolve(),
        shouldRefreshAddresses
          ? this.fetchAddresses({ force: true, page: this.addressesPage })
          : Promise.resolve(),
      ]);
    },

    async fetchAddresses(
      params: {
        vaultId?: string;
        size?: number;
        force?: boolean;
        page?: number;
      } = {}
    ) {
      const vaultId = params.vaultId || process.env.VUE_APP_VAULT_ID || "main";
      const pageSize =
        params.size ?? this.addressesPageSize ?? DEFAULT_ADDRESSES_PAGE_SIZE;
      const targetPage = Math.max(0, params.page ?? this.addressesPage ?? 0);
      const force = params.force === true;

      if (this.addressesLoading && !force) {
        return;
      }

      const colRef = collection(
        firestore,
        "systemVaults",
        vaultId,
        "addresses"
      );

      if (targetPage === 0) {
        addressesCursors = [];
      }

      const ensureCursorForPage = async (page: number) => {
        if (page <= 0) return undefined;
        if (addressesCursors[page - 1]) return addressesCursors[page - 1];

        let nearestKnown = -1;
        for (let i = addressesCursors.length - 1; i >= 0; i -= 1) {
          if (addressesCursors[i]) {
            nearestKnown = i + 1;
            break;
          }
        }

        let currentPage = Math.max(0, nearestKnown);
        let cursorRef =
          currentPage > 0 ? addressesCursors[currentPage - 1] : undefined;

        while (currentPage < page) {
          const constraints: QueryConstraint[] = [];
          constraints.push(orderBy("dateCreated", "desc"));
          if (cursorRef) {
            constraints.push(startAfter(cursorRef));
          }
          constraints.push(limit(pageSize));

          const snap = await getDocs(query(colRef, ...constraints));
          const docs = snap.docs;
          addressesCursors[currentPage] =
            docs.length > 0
              ? (docs[docs.length - 1] as QueryDocumentSnapshot<DocumentData>)
              : undefined;
          cursorRef = addressesCursors[currentPage];
          currentPage += 1;
          if (!cursorRef) break;
        }

        return addressesCursors[page - 1];
      };

      this.addressesLoading = true;

      try {
        const cursor = await ensureCursorForPage(targetPage);

        const constraints: QueryConstraint[] = [];
        constraints.push(orderBy("dateCreated", "desc"));
        if (cursor) {
          constraints.push(startAfter(cursor));
        }
        constraints.push(limit(pageSize));

        const [snap, countSnapshot] = await Promise.all([
          getDocs(query(colRef, ...constraints)),
          getCountFromServer(query(colRef)),
        ]);
        const docs = snap.docs;
        const totalCount = countSnapshot.data().count;

        addressesCursors[targetPage] =
          docs.length > 0
            ? (docs[docs.length - 1] as QueryDocumentSnapshot<DocumentData>)
            : undefined;

        this.addresses = docs.map((d) => {
          const addr = d.data() as VaultAddress;
          return {
            ...addr,
            id: addr.id || d.id,
            dateCreated: toDate(addr.dateCreated),
            dateUpdated: toDate(addr.dateUpdated),
          } as VaultAddressUI;
        });
        this.addressesUpdatedAt = new Date();
        this.addressesHasMore = (targetPage + 1) * pageSize < totalCount;
        this.addressesPage = targetPage;
        this.addressesPageSize = pageSize;
        this.addressesTotalCount = totalCount;
        this.addressesLoading = false;
      } catch (error) {
        console.error("Error loading vault addresses", error);
        this.addressesLoading = false;
      }
    },

    async fetchTransactions(
      params: {
        vaultId?: string;
        size?: number;
        force?: boolean;
        page?: number;
      } = {}
    ) {
      const vaultId = params.vaultId || process.env.VUE_APP_VAULT_ID || "main";
      const pageSize =
        params.size ??
        this.transactionsPageSize ??
        DEFAULT_TRANSACTIONS_PAGE_SIZE;
      const targetPage = Math.max(0, params.page ?? this.transactionsPage ?? 0);
      const force = params.force === true;

      if (this.transactionsLoading && !force) {
        return;
      }

      const colRef = collection(
        firestore,
        "systemVaults",
        vaultId,
        "transactions"
      );

      if (targetPage === 0) {
        transactionsCursors = [];
      }

      const ensureCursorForPage = async (page: number) => {
        if (page <= 0) return undefined;
        if (transactionsCursors[page - 1]) return transactionsCursors[page - 1];

        let nearestKnown = -1;
        for (let i = transactionsCursors.length - 1; i >= 0; i -= 1) {
          if (transactionsCursors[i]) {
            nearestKnown = i + 1;
            break;
          }
        }

        let currentPage = Math.max(0, nearestKnown);
        let cursorRef =
          currentPage > 0 ? transactionsCursors[currentPage - 1] : undefined;

        while (currentPage < page) {
          const constraints: QueryConstraint[] = [];
          constraints.push(orderBy("createdTimestamp", "desc"));
          if (cursorRef) {
            constraints.push(startAfter(cursorRef));
          }
          constraints.push(limit(pageSize));

          const snap = await getDocs(query(colRef, ...constraints));
          const docs = snap.docs;
          transactionsCursors[currentPage] =
            docs.length > 0
              ? (docs[docs.length - 1] as QueryDocumentSnapshot<DocumentData>)
              : undefined;
          cursorRef = transactionsCursors[currentPage];
          currentPage += 1;
          if (!cursorRef) break;
        }

        return transactionsCursors[page - 1];
      };

      this.transactionsLoading = true;

      try {
        const cursor = await ensureCursorForPage(targetPage);

        const constraints: QueryConstraint[] = [];
        constraints.push(orderBy("createdTimestamp", "desc"));
        if (cursor) {
          constraints.push(startAfter(cursor));
        }
        constraints.push(limit(pageSize));

        const [snap, countSnapshot] = await Promise.all([
          getDocs(query(colRef, ...constraints)),
          getCountFromServer(query(colRef)),
        ]);
        const docs = snap.docs;
        const totalCount = countSnapshot.data().count;

        transactionsCursors[targetPage] =
          docs.length > 0
            ? (docs[docs.length - 1] as QueryDocumentSnapshot<DocumentData>)
            : undefined;

        const transactions: Array<VaultTransactionUI> = docs.map((d) => {
          const tx: any = d.data();
          const recipients = Array.isArray(tx.recipients) ? tx.recipients : [];
          const amountNum =
            recipients.reduce((sum: number, r: any) => {
              if (tx.direction === "outgoing" && r.addressType !== "change") {
                return sum + Number(r.amount || 0);
              }
              if (tx.direction === "incoming" && r.addressType === "deposit") {
                return sum + Number(r.amount || 0);
              }
              return sum;
            }, 0) ||
            Number(tx.amount ?? tx.totalAmount ?? 0) ||
            0;
          const createdSource =
            typeof tx.createdTimestamp === "number"
              ? tx.createdTimestamp * 1000
              : tx.createdTimestamp || tx.dateCreated;
          return {
            id: tx.transactionHash || d.id,
            date: toDate(createdSource),
            direction: tx.direction,
            status: tx.status,
            amount: amountNum.toFixed(8),
            hash: tx.transactionId || tx.transactionHash,
          };
        });

        this.transactions = transactions;
        this.transactionsUpdatedAt = new Date();
        this.transactionsHasMore = (targetPage + 1) * pageSize < totalCount;
        this.transactionsPage = targetPage;
        this.transactionsPageSize = pageSize;
        this.transactionsTotalCount = totalCount;
        this.transactionsLoading = false;
      } catch (error) {
        console.error("Error loading vault transactions", error);
        this.transactionsLoading = false;
      }
    },
  },
});
